"use client";

import { useTranslations } from "next-intl";
import { StatusBadge } from "@/components/ui/status-badge";
import { formatLocalPrice, formatPrice } from "@/lib/currency";
import type { Locale } from "@/lib/i18n";
import { useLearnPriceRegion } from "@/features/learn/lib/price-region";
import type { LearnTrackMoney, LearnTrackPrice } from "@/features/learn/lib/track-price";

export type ShownTrackPrice = {
  amount: string;
  list: string | null;
  percentOff: number;
};

/** The track's price in the visitor's currency (`LN-BUY01`): dollars only outside Bangladesh and only when the plan is sold in dollars. */
export function useShownTrackPrice(price: LearnTrackPrice | null, locale: Locale): ShownTrackPrice | null {
  const region = useLearnPriceRegion();
  if (!price) return null;
  const inUsd = region === "intl" && price.usd !== null;
  const money: LearnTrackMoney = inUsd && price.usd ? price.usd : price.bdt;
  const format = (amount: number) =>
    inUsd ? formatPrice(amount, "USD") : formatLocalPrice(amount, "BDT", locale);
  return {
    amount: format(money.amount),
    list: money.list === null ? null : format(money.list),
    percentOff: money.percentOff,
  };
}

/**
 * The price chip under the track page's crumb. With one paid plan it reads
 * "৳1,999 once"; otherwise it keeps the server's own line ("From …" or "Free to
 * start"), passed in as `fallback`.
 */
export function LearnTrackPriceChip({
  price,
  locale,
  fallback,
}: {
  price: LearnTrackPrice | null;
  locale: Locale;
  fallback: string;
}) {
  const t = useTranslations("learn");
  const shown = useShownTrackPrice(price, locale);
  return <StatusBadge label={shown ? t("priceOnceChip", { price: shown.amount }) : fallback} tone="success" />;
}
